import { useState, type ReactNode } from 'react';
import { Button } from '../Button/index.js';
import { useI18nT } from '../../i18n/index.js';
import { Dialog, type DialogSize, type DialogTone } from './Dialog.js';

export type ConfirmTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

function DefaultDangerIcon(): ReactNode {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M10 2.5 18 16.5 H2 Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <rect x="9.1" y="7.5" width="1.8" height="5.2" rx="0.9" fill="currentColor" />
      <rect x="9.1" y="13.5" width="1.8" height="1.8" rx="0.9" fill="currentColor" />
    </svg>
  );
}
function DefaultInfoIcon(): ReactNode {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.6" />
      <rect x="9.1" y="8.4" width="1.8" height="6" rx="0.9" fill="currentColor" />
      <rect x="9.1" y="5.2" width="1.8" height="1.8" rx="0.9" fill="currentColor" />
    </svg>
  );
}

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: ReactNode;
  children?: ReactNode;
  /** Confirm button label. Defaults to i18n dialog.confirm.confirmBtn. */
  confirmLabel?: ReactNode;
  /** Cancel button label. Defaults to i18n common.cancel. */
  cancelLabel?: ReactNode;
  /** Visual tone — drives header badge color + confirm button variant. */
  tone?: ConfirmTone;
  /** Override the default tone badge (triangle for danger/warning, ⓘ for info). */
  toneIcon?: ReactNode;
  /** Async-safe: if returns a promise, the confirm button shows loading until
   *  it resolves, and the dialog only closes on resolve. Throwing/rejecting
   *  leaves the dialog open. */
  onConfirm: () => void | Promise<void>;
  onCancel?: () => void;
  size?: DialogSize;
}

function defaultToneIcon(tone: ConfirmTone): ReactNode | undefined {
  if (tone === 'danger' || tone === 'warning') return <DefaultDangerIcon />;
  if (tone === 'info' || tone === 'success') return <DefaultInfoIcon />;
  return undefined;
}

/** Prebuilt confirm/cancel modal. Handles the loading-during-async-confirm
 *  pattern so the caller doesn't re-implement it every time. */
export function ConfirmDialog(props: ConfirmDialogProps): ReactNode {
  const t = useI18nT();
  const {
    open,
    onOpenChange,
    title = t('dialog.confirm.title'),
    children = t('dialog.confirm.body'),
    confirmLabel = t('dialog.confirm.confirmBtn'),
    cancelLabel = t('common.cancel'),
    tone = 'danger',
    toneIcon,
    onConfirm,
    onCancel,
    size = 'sm',
  } = props;

  const dialogTone: DialogTone = tone;
  const resolvedIcon = toneIcon ?? defaultToneIcon(tone);

  const [loading, setLoading] = useState(false);

  const handleConfirm = (): void => {
    const result = onConfirm();
    if (result instanceof Promise) {
      setLoading(true);
      result
        .then(() => {
          setLoading(false);
          onOpenChange(false);
        })
        .catch(() => {
          setLoading(false);
        });
    } else {
      onOpenChange(false);
    }
  };

  const handleCancel = (): void => {
    onCancel?.();
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (loading) return;
        if (!next) onCancel?.();
        onOpenChange(next);
      }}
      title={title}
      size={size}
      tone={dialogTone}
      toneIcon={resolvedIcon}
      showClose={!loading}
      closeOnBackdrop={!loading}
      closeOnEscape={!loading}
      footer={
        <>
          <Button variant="secondary" onClick={handleCancel} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            variant={tone === 'danger' ? 'danger' : 'primary'}
            onClick={handleConfirm}
            loading={loading}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {children}
    </Dialog>
  );
}
